import { ZonedDateTime } from "@internationalized/date";
import cx from "classnames";
import { useCallback } from "react";
import {
  Calendar,
  DateInput,
  DatePicker,
  Dialog,
  Group,
  Label,
  Popover,
  TimeField,
  TimeValue,
} from "react-aria-components";

import { Calendar as CalendarIcon } from "components/icons/generated";

import Box from "../Box";
import ButtonIcon from "../ButtonIcon";
import DatepickerCalendarContent from "./CalendarContent";
import CustomDateSegment from "./CustomDateSegment";
import { formatAbsoluteToZonedDateTime, getDateParts } from "./helpers";
import styles from "./styles.module.css";
import { DatepickerProps } from "./types";

const adEraStart = new Date(Date.parse("0001-01-01"));

const Datepicker = ({
  date = new Date(),
  onChange,
  minDate = adEraStart,
  maxDate,
  withTime,
  hasError,
  disabled,
  placement = "bottom end",
  fullWidth,
  renderTimezone,
}: DatepickerProps) => {
  const minZonedDate = formatAbsoluteToZonedDateTime(minDate, renderTimezone);
  const maxZonedDate = maxDate && formatAbsoluteToZonedDateTime(maxDate, renderTimezone);

  const { zonedDateTime, time } = getDateParts(date, renderTimezone);

  const onDateChange = (value: ZonedDateTime | null) => {
    if (value?.era === "AD") {
      onChange(value.toDate());
    }
  };

  const onTimeChange = useCallback(
    (next: TimeValue | null) => {
      if (!next) return;
      const nextDate = zonedDateTime
        .set({ hour: next.hour ?? 0, minute: next.minute ?? 0 })
        .toDate();
      if (nextDate.getTime() === date.getTime()) return;
      onChange(nextDate);
    },
    [date, onChange, zonedDateTime]
  );

  return (
    <DatePicker
      onChange={onDateChange}
      value={zonedDateTime}
      granularity={withTime ? "minute" : "day"}
      aria-label="Choose date"
    >
      <Box direction="column" gap="small" align="start">
        <Group
          isDisabled={disabled}
          isInvalid={hasError}
          className={cx(styles.input, {
            [styles.fullWidth]: fullWidth,
          })}
        >
          <DateInput className={styles.inputInner}>
            {(segment) => <CustomDateSegment segment={segment} value={zonedDateTime} />}
          </DateInput>
          <ButtonIcon icon={CalendarIcon} variant="ghost">
            Open datepicker
          </ButtonIcon>
        </Group>
      </Box>

      <Popover placement={placement}>
        <Dialog className={styles.dialog}>
          <Box direction="column" align="center" justify="center">
            <Calendar className={styles.calendar} minValue={minZonedDate} maxValue={maxZonedDate}>
              <DatepickerCalendarContent />
            </Calendar>
            {withTime && (
              <TimeField
                className={cx(styles.timeField, styles.timeWrapper)}
                value={time}
                onChange={onTimeChange}
              >
                <Label className={styles.timeLabel}>Time</Label>
                <DateInput className={cx(styles.input, styles.timeInput)}>
                  {(segment) => <CustomDateSegment segment={segment} value={zonedDateTime} />}
                </DateInput>
              </TimeField>
            )}
          </Box>
        </Dialog>
      </Popover>
    </DatePicker>
  );
};

Datepicker.displayName = "DS.Datepicker";

export default Datepicker;
